/**
 * Step wall-time stats (microseconds), per sim step.
 *
 * Problems this module solves:
 * - `performance.now()` often returns 0 for a single cheap step (timer quantum),
 *   which made min/median show as 0000 and look broken.
 * - Multi-step frames (2×/4×/8×) must report cost *per step*, not the whole batch.
 * - Plain mean over ~2s of frames bounced; multi-minute EMAs stay readable.
 *
 * Strategy: coalesce tiny/zero measurements until we have a measurable batch,
 * then push (totalMs / steps) as one per-step sample. Rolling window → last/min/
 * median/max. Wall-clock EMAs at 1 / 5 / 10 minutes for load trend.
 */

export type StepAggregates = {
  count: number;
  /** Most recent coalesced per-step sample (µs) */
  last: number;
  min: number;
  max: number;
  median: number;
  /** ~1-minute time-constant EMA (µs) */
  ema1m: number;
  /** ~5-minute time-constant EMA (µs) */
  ema5m: number;
  /** ~10-minute time-constant EMA (µs) */
  ema10m: number;
};

const TAU_1M = 60;
const TAU_5M = 300;
const TAU_10M = 600;

/**
 * Hold samples until total measured time reaches this (ms), or step count hits
 * MAX_HOLD_STEPS. ~0.05 ms is above typical timer floors while still responsive.
 */
const MIN_HOLD_MS = 0.05;
const MAX_HOLD_STEPS = 32;

/** Character width for fixed µs fields (pad so HUD doesn’t reflow). */
export const US_FIELD_WIDTH = 6;

export class StepTimingStats {
  private readonly buf: Float64Array;
  private readonly capacity: number;
  private write = 0;
  private filled = 0;
  private lastUs = 0;

  private ema1m = 0;
  private ema5m = 0;
  private ema10m = 0;
  private emaReady = false;
  /** performance.now() seconds of last accepted sample, or null before first */
  private lastWallSec: number | null = null;

  /** Coalesce sub-quantum measurements */
  private holdMs = 0;
  private holdSteps = 0;

  constructor(capacity = 120) {
    this.capacity = Math.max(8, capacity | 0);
    this.buf = new Float64Array(this.capacity);
  }

  /**
   * Record wall time for `steps` sim steps (milliseconds from performance.now delta).
   * Stores **per-step** µs after coalescing tiny/zero readings.
   */
  pushMs(
    ms: number,
    steps: number = 1,
    wallNowMs: number = performance.now(),
  ): void {
    const n = Math.max(1, steps | 0);
    const dt = Number.isFinite(ms) && ms > 0 ? ms : 0;
    this.holdMs += dt;
    this.holdSteps += n;

    if (this.holdMs < MIN_HOLD_MS && this.holdSteps < MAX_HOLD_STEPS) {
      return;
    }
    if (this.holdSteps <= 0) return;

    // If still zero after MAX_HOLD_STEPS, timer can’t resolve — record a floor
    // of 0.5 µs/step so stats stay honest without looking “stuck at blank”.
    const perStepMs =
      this.holdMs > 0 ? this.holdMs / this.holdSteps : 0.0005 / 1000;
    this.commitUs(perStepMs * 1000, wallNowMs);
    this.holdMs = 0;
    this.holdSteps = 0;
  }

  /** @deprecated prefer pushMs(ms, steps) — kept for simple tests */
  pushSampleMs(ms: number, wallNowMs: number = performance.now()): void {
    this.pushMs(ms, 1, wallNowMs);
  }

  private commitUs(us: number, wallNowMs: number): void {
    const sample = Math.max(0, us);
    this.lastUs = sample;
    this.buf[this.write] = sample;
    this.write = (this.write + 1) % this.capacity;
    if (this.filled < this.capacity) this.filled++;

    const wallSec = wallNowMs / 1000;
    if (!this.emaReady || this.lastWallSec === null) {
      this.ema1m = sample;
      this.ema5m = sample;
      this.ema10m = sample;
      this.emaReady = true;
    } else {
      const dt = Math.max(0, wallSec - this.lastWallSec);
      this.ema1m = emaStep(this.ema1m, sample, dt, TAU_1M);
      this.ema5m = emaStep(this.ema5m, sample, dt, TAU_5M);
      this.ema10m = emaStep(this.ema10m, sample, dt, TAU_10M);
    }
    this.lastWallSec = wallSec;
  }

  clear(): void {
    this.write = 0;
    this.filled = 0;
    this.lastUs = 0;
    this.ema1m = 0;
    this.ema5m = 0;
    this.ema10m = 0;
    this.emaReady = false;
    this.lastWallSec = null;
    this.holdMs = 0;
    this.holdSteps = 0;
    this.buf.fill(0);
  }

  get count(): number {
    return this.filled;
  }

  aggregates(): StepAggregates | null {
    const n = this.filled;
    if (n === 0) return null;

    let min = Infinity;
    let max = -Infinity;
    const snap = new Float64Array(n);
    // Ring order: oldest → newest (correct after wrap; stats don’t need order,
    // but this avoids reading unfilled slots if capacity logic ever changes).
    const start = this.filled < this.capacity ? 0 : this.write;
    for (let i = 0; i < n; i++) {
      const v = this.buf[(start + i) % this.capacity]!;
      snap[i] = v;
      if (v < min) min = v;
      if (v > max) max = v;
    }

    snap.sort();
    const median =
      n % 2 === 1
        ? snap[(n - 1) >> 1]!
        : 0.5 * (snap[n / 2 - 1]! + snap[n / 2]!);

    return {
      count: n,
      last: this.lastUs,
      min,
      max,
      median,
      ema1m: this.ema1m,
      ema5m: this.ema5m,
      ema10m: this.ema10m,
    };
  }
}

function emaStep(
  prev: number,
  sample: number,
  dtSec: number,
  tauSec: number,
): number {
  if (dtSec <= 0 || tauSec <= 0) return prev;
  const alpha = 1 - Math.exp(-dtSec / tauSec);
  return alpha * sample + (1 - alpha) * prev;
}

/**
 * Fixed-width µs field for tabular HUD.
 * Under 100 µs → one decimal (e.g. `012.3`); else integer (`001234` grows past width).
 */
export function formatUsField(
  us: number,
  width: number = US_FIELD_WIDTH,
): string {
  if (!Number.isFinite(us)) return "—".padStart(width);
  const x = Math.max(0, us);
  if (x < 100) {
    // "12.3" → pad to width
    return x.toFixed(1).padStart(width, "0");
  }
  return Math.round(x).toString().padStart(width, "0");
}

/** Same as formatUsField plus unit. */
export function formatDurationUs(
  us: number,
  width: number = US_FIELD_WIDTH,
): string {
  return `${formatUsField(us, width)} µs`;
}

/** Compact one-line summary for the transport HUD (fixed-width fields). */
export function formatStepAggregates(a: StepAggregates): string {
  const w = US_FIELD_WIDTH;
  // Keep short: long min/med/max strings forced the toolbar onto a second row.
  // Full detail still available via the HUD title attribute.
  return (
    `step ${formatUsField(a.last, w)}` +
    ` · 1m ${formatUsField(a.ema1m, w)}` +
    ` · med ${formatUsField(a.median, w)}` +
    ` · max ${formatUsField(a.max, w)}` +
    ` µs`
  );
}

/** Longer HUD string for title/tooltip (all EMA + extremes). */
export function formatStepAggregatesFull(a: StepAggregates): string {
  const w = US_FIELD_WIDTH;
  return (
    `step ${formatUsField(a.last, w)}` +
    ` · 1m ${formatUsField(a.ema1m, w)}` +
    ` · 5m ${formatUsField(a.ema5m, w)}` +
    ` · 10m ${formatUsField(a.ema10m, w)}` +
    ` · min ${formatUsField(a.min, w)}` +
    ` · med ${formatUsField(a.median, w)}` +
    ` · max ${formatUsField(a.max, w)}` +
    ` µs/step` +
    ` (n=${String(a.count).padStart(3, "0")})`
  );
}
